/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-student
 * @responsibility Serialize the SET-F10 organization-session password-change wire command and
 *   response, and read the double-submit CSRF cookie the organization-session flow requires.
 * @state-owner authenticated API; the browser's own non-httpOnly CSRF cookie
 * @allowed-imports API client public type; @vispo/contracts; browser document.cookie
 * @forbidden React; UI role inference; the httpOnly session cookie (never readable from script)
 * @public-api changeOrganizationPassword; readOrganizationCsrfToken
 * @side-effects authenticated HTTP request; reads document.cookie
 * @tests frontend/packages/workspace-web/src/modules/settings-student/components/StudentSettingsSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase, EXCEPT change_organization_password's own request/response fields, which
 *   are genuinely snake_case on the wire (identity_access/schemas.py has no alias_generator and no
 *   per-field Field(alias=...) on OrganizationPasswordChangeCommand/OrganizationSessionResponse --
 *   confirmed by direct read, not assumed); never the caller's role and never a server-side key
 */

import type { VispoApiClient } from '@vispo/api-client';
import {
  type ChangeOrganizationPasswordInput,
  changeOrganizationPasswordInputSchema,
  organizationSessionResponseSchema,
} from '@vispo/contracts';

const ORGANIZATION_CSRF_COOKIE = 'vispo_org_csrf';

/**
 * The organization-session CSRF cookie is deliberately non-httpOnly (a double-submit-cookie
 * defense: the server requires the same value back as the X-CSRF-Token header, which a
 * cross-site request cannot read). Reading it here, per call, avoids threading it through
 * SessionProvider's own context -- that file is not in this card's restricted-file approval.
 */
export function readOrganizationCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${ORGANIZATION_CSRF_COOKIE}=`;
  for (const entry of document.cookie.split('; ')) {
    if (entry.startsWith(prefix)) return decodeURIComponent(entry.slice(prefix.length));
  }
  return null;
}

export function changeOrganizationPassword(
  apiClient: VispoApiClient,
  input: ChangeOrganizationPasswordInput,
  csrfToken: string,
) {
  return apiClient
    .request<unknown>('/api/v1/auth/password/change', {
      method: 'POST',
      body: changeOrganizationPasswordInputSchema.parse(input),
      headers: { 'X-CSRF-Token': csrfToken },
    })
    .then((response) => organizationSessionResponseSchema.parse(response));
}
